import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { apiFetch } from '../../config/apiClient';

/** A test package as returned by learning-service. */
interface TestPackage {
  id: string;
  name: string;
  grade: string[];
  subject: string[];
  series: string[];
  openingDate?: string;
  closingDate?: string;
}

/** One series, with the window shared by its packages. */
interface SeriesRow {
  series: string;
  packages: TestPackage[];
  openingDate?: string;
  closingDate?: string;
  /** True when the packages in this series do not all share one window. */
  mixed: boolean;
}

const toDateInput = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const fromDateInput = (value: string, endOfDay: boolean) =>
  value ? new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`).toISOString() : undefined;

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const statusOf = (row: SeriesRow): { label: string; color: 'success' | 'default' | 'warning' } => {
  if (!row.openingDate || !row.closingDate) return { label: 'Not scheduled', color: 'warning' };
  const now = Date.now();
  if (now < Date.parse(row.openingDate)) return { label: 'Upcoming', color: 'default' };
  if (now > Date.parse(row.closingDate)) return { label: 'Closed', color: 'default' };
  return { label: 'Open now', color: 'success' };
};

interface SeriesPageProps {
  /** True when rendered inside a tab, where the surrounding page supplies the heading and padding. */
  embedded?: boolean;
  /** Scope to one academic year; null shows every year. */
  academicYear?: number | null;
}

const SeriesPage: React.FC<SeriesPageProps> = ({ embedded = false, academicYear = null }) => {
  const [packages, setPackages] = useState<TestPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SeriesRow | null>(null);
  const [form, setForm] = useState({ openingDate: '', closingDate: '' });
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const yearParam = academicYear != null ? `?academicYear=${academicYear}` : '';
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/packages${yearParam}`, { headers: authHeaders() });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.message || 'Failed to load test series');
      setPackages(body?.data?.packages ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test series');
    } finally {
      setLoading(false);
    }
  }, [academicYear]);

  useEffect(() => {
    load();
  }, [load]);

  // Group packages by series and work out whether each series has one shared window.
  const rows = useMemo<SeriesRow[]>(() => {
    const bySeries = new Map<string, TestPackage[]>();
    packages.forEach((pack) => {
      const name = pack.series?.[0];
      if (!name) return;
      bySeries.set(name, [...(bySeries.get(name) ?? []), pack]);
    });

    return Array.from(bySeries.entries())
      .map(([series, packs]) => {
        const windows = new Set(packs.map((p) => `${p.openingDate}|${p.closingDate}`));
        return {
          series,
          packages: packs,
          openingDate: packs[0]?.openingDate,
          closingDate: packs[0]?.closingDate,
          mixed: windows.size > 1,
        };
      })
      .sort((a, b) => (a.openingDate || '').localeCompare(b.openingDate || ''));
  }, [packages]);

  const openEdit = (row: SeriesRow) => {
    setEditing(row);
    setForm({ openingDate: toDateInput(row.openingDate), closingDate: toDateInput(row.closingDate) });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        openingDate: fromDateInput(form.openingDate, false),
        closingDate: fromDateInput(form.closingDate, true),
      });
      // The window belongs to the series, so every package in it moves together.
      const results = await Promise.all(
        editing.packages.map((pack) =>
          apiFetch(`${API_ENDPOINTS.learningApi}/packages/${pack.id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body,
          })
        )
      );
      const failed = results.filter((r) => !r.ok).length;
      if (failed > 0) throw new Error(`${failed} of ${results.length} tests failed to update`);
      setSavedNote(`${editing.series}: ${results.length} tests rescheduled`);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Test Series
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        When each series opens and closes. Changing a window moves every test in that series,
        across all grades and subjects.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {savedNote && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedNote(null)}>{savedNote}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Series', 'Status', 'Opens', 'Closes', 'Tests', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const status = statusOf(row);
                return (
                  <TableRow key={row.series} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.series}</TableCell>
                    <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                    <TableCell>{toDateInput(row.openingDate) || '—'}</TableCell>
                    <TableCell>{toDateInput(row.closingDate) || '—'}</TableCell>
                    <TableCell>
                      {row.packages.length}
                      {row.mixed && (
                        <Chip size="small" color="warning" label="mixed dates" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEdit(row)}>Reschedule</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No test series found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reschedule {editing?.series}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies to all {editing?.packages.length} tests in this series.
            {editing?.mixed && ' These tests currently have different windows; saving puts them all on the same one.'}
          </Typography>
          <TextField
            fullWidth type="date" label="Opens" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            value={form.openingDate} onChange={(e) => setForm({ ...form, openingDate: e.target.value })}
          />
          <TextField
            fullWidth type="date" label="Closes" InputLabelProps={{ shrink: true }}
            value={form.closingDate} onChange={(e) => setForm({ ...form, closingDate: e.target.value })}
            helperText="Closing runs to the end of the day"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.openingDate || !form.closingDate}
          >
            {saving ? 'Saving...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeriesPage;
